import { useTranslation } from 'react-i18next'
import { highlightSearchTerm } from '@/utils/clientSearch'
import type { ApiSearchResult, GetContentBundleResponse } from '@/types/api'
import { useAppStore } from '@/store/appStore'

interface SearchResultItemProps {
  result: ApiSearchResult
  query: string
  bundle: GetContentBundleResponse | undefined
  onNavigate: (path: string) => void
}

// Maps SearchResultContentType to overlay type
const CONTENT_TYPE_TO_OVERLAY: Record<string, 'overview' | 'appendices' | 'release-notes' | 'faq' | 'crosswalks'> = {
  'Overview': 'overview',
  'Appendices': 'appendices',
  'ReleaseNotes': 'release-notes',
  'Faq': 'faq',
  'Crosswalks': 'crosswalks',
}

export function SearchResultItem({ result, query, bundle, onNavigate }: SearchResultItemProps) {
  const { t } = useTranslation()
  const { setOverlayPage } = useAppStore()

  const handleClick = () => {
    // Handle AdditionalReadingContent categories - open flyout with hash
    if (result.contentType in CONTENT_TYPE_TO_OVERLAY) {
      const overlayType = CONTENT_TYPE_TO_OVERLAY[result.contentType]
      setOverlayPage({ type: overlayType, hash: `#reading-${result.entityId}` })
      return
    }

    // Handle traditional page navigation
    const highlightHash = query ? `#highlight=${encodeURIComponent(query)}` : ''
    if (result.contentType === 'PracticeArea') {
      onNavigate(`/practice-area/${result.entityId}${highlightHash}`)
    } else if (result.contentType === 'Practice') {
      onNavigate(`/practice/${result.entityId}${highlightHash}`)
    } else if (result.contentType === 'Framework') {
      onNavigate(`/framework/${result.entityId}`)
    } else if (result.contentType === 'GlossaryTerm') {
      onNavigate(`/glossary#term-${result.entityId}`)
    } else if (result.contentType === 'ContextualContent') {
      const ccHighlight = query ? `&highlight=${encodeURIComponent(query)}` : ''
      if (result.parentPracticeId) {
        onNavigate(`/practice/${result.parentPracticeId}#cc-${result.entityId}${ccHighlight}`)
      } else if (result.practiceAreaId) {
        onNavigate(`/practice-area/${result.practiceAreaId}#cc-${result.entityId}${ccHighlight}`)
      }
    }
  }

  const practiceArea =
    result.practiceAreaId && bundle
      ? bundle.practiceAreas.find((pa) => pa.id === result.practiceAreaId)
      : null

  return (
    <div
      className="search-result-item"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() }}
    >
      <div className="search-result-meta">
        <span className="search-result-type">{t(`search.contentType_${result.contentType}`)}</span>
        {practiceArea && (
          <span className="search-result-pa">
            {result.contentType === 'Practice'
              ? `${practiceArea.acronym}: ${practiceArea.displayName}`
              : practiceArea.acronym}
          </span>
        )}
      </div>
      <div
        className="search-result-title"
        dangerouslySetInnerHTML={{ __html: highlightSearchTerm(result.title, query) }}
      />
      {result.snippet && (
        <div
          className="search-result-snippet"
          dangerouslySetInnerHTML={{ __html: highlightSearchTerm(result.snippet, query) }}
        />
      )}
    </div>
  )
}
